import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  FaceSmileIcon,
  PhoneIcon,
  VideoCameraIcon,
  EllipsisVerticalIcon,
  PlusIcon
} from '@heroicons/react/24/outline'

const ChatPage: React.FC = () => {
  const [selectedChat, setSelectedChat] = useState(1)
  const [message, setMessage] = useState('')

  const conversations = [
    {
      id: 1,
      name: 'Sarah Miller',
      lastMessage: 'That sounds amazing! When are you free?',
      time: '2m ago',
      unread: 2,
      online: true,
      avatar: ''
    },
    {
      id: 2,
      name: 'Mike Johnson',
      lastMessage: 'Thanks for the recommendation!',
      time: '1h ago',
      unread: 0,
      online: true,
      avatar: ''
    },
    {
      id: 3,
      name: 'Emma Davis',
      lastMessage: 'See you tomorrow!',
      time: '3h ago',
      unread: 1,
      online: false,
      avatar: ''
    },
    {
      id: 4,
      name: 'Alex Chen',
      lastMessage: 'The photos look great',
      time: '1d ago',
      unread: 0,
      online: false,
      avatar: ''
    }
  ]

  const messages = [
    {
      id: 1,
      sender: 'them',
      text: 'Hey! How was your day?',
      time: '10:30 AM'
    },
    {
      id: 2,
      sender: 'me',
      text: 'It was great! Just finished an amazing hike. The views were incredible!',
      time: '10:32 AM'
    },
    {
      id: 3,
      sender: 'them',
      text: 'That sounds wonderful! I love hiking too. Where did you go?',
      time: '10:35 AM'
    },
    {
      id: 4,
      sender: 'me',
      text: 'Mount Rainier! Have you been there?',
      time: '10:36 AM'
    },
    {
      id: 5,
      sender: 'them',
      text: 'No, but it\'s on my bucket list! Maybe we could go together sometime?',
      time: '10:38 AM'
    },
    {
      id: 6,
      sender: 'me',
      text: 'That sounds amazing! When are you free?',
      time: '10:40 AM'
    }
  ]

  const handleSend = () => {
    if (message.trim()) {
      console.log('Sending:', message)
      setMessage('')
    }
  }

  const selectedConversation = conversations.find(c => c.id === selectedChat)

  return (
    <div className="h-[calc(100vh-12rem)] flex gap-6">
      {/* Conversations List */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="w-96 card-glass flex flex-col"
      >
        {/* Search */}
        <div className="p-4 border-b border-white/10">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
            <input
              type="text"
              placeholder="Search conversations..."
              className="input-glass w-full pl-10 py-2"
            />
          </div>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {conversations.map((conv, index) => (
            <ConversationItem
              key={conv.id}
              conversation={conv}
              isActive={selectedChat === conv.id}
              onClick={() => setSelectedChat(conv.id)}
              index={index}
            />
          ))}
        </div>

        {/* New Chat Button */}
        <div className="p-4 border-t border-white/10">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="btn-primary w-full flex items-center justify-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>New Conversation</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Chat Area */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="flex-1 card-glass flex flex-col"
      >
        {/* Chat Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600" />
              {selectedConversation?.online && (
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-dark-100" />
              )}
            </div>
            <div>
              <h2 className="font-bold text-lg">{selectedConversation?.name}</h2>
              <p className="text-sm text-white/70">
                {selectedConversation?.online ? 'Active now' : 'Offline'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <PhoneIcon className="h-6 w-6" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <VideoCameraIcon className="h-6 w-6" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <EllipsisVerticalIcon className="h-6 w-6" />
            </motion.button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
          {messages.map((msg, index) => (
            <MessageBubble key={msg.id} message={msg} index={index} />
          ))}
        </div>

        {/* Message Input */}
        <div className="p-6 border-t border-white/10">
          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <PhotoIcon className="h-6 w-6" />
            </motion.button>
            
            <div className="flex-1 relative">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type a message..."
                className="input-glass w-full pr-12"
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-white/10 rounded-lg transition-colors">
                <FaceSmileIcon className="h-5 w-5" />
              </button>
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleSend}
              className="p-3 bg-primary-600 rounded-xl"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

interface ConversationItemProps {
  conversation: any
  isActive: boolean
  onClick: () => void
  index: number
}

const ConversationItem: React.FC<ConversationItemProps> = ({ conversation, isActive, onClick, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.05 }}
    onClick={onClick}
    className={`p-4 cursor-pointer transition-all hover:bg-white/5 ${
      isActive ? 'bg-white/10' : ''
    }`}
  >
    <div className="flex items-center space-x-4">
      <div className="relative">
        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600" />
        {conversation.online && (
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-dark-100" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold truncate">{conversation.name}</h3>
          <span className="text-xs text-white/50">{conversation.time}</span>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-white/70 truncate">{conversation.lastMessage}</p>
          {conversation.unread > 0 && (
            <span className="ml-2 bg-primary-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
              {conversation.unread}
            </span>
          )}
        </div>
      </div>
    </div>
  </motion.div>
)

interface MessageBubbleProps {
  message: any
  index: number
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    className={`flex ${message.sender === 'me' ? 'justify-end' : 'justify-start'}`}
  >
    <div className={`max-w-md ${message.sender === 'me' ? 'chat-bubble-sent' : 'chat-bubble-received'}`}>
      <p>{message.text}</p>
      <p className="text-xs opacity-70 mt-1">{message.time}</p>
    </div>
  </motion.div>
)

export default ChatPage